import { Injectable, OnDestroy, inject } from '@angular/core';
import { Subscription, of, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { PermissionService } from './permission.service';
import { PlatformNotificationService } from './platform-notification.service';

/**
 * Keeps RBAC menus and session state in sync when WebSocket events are missed
 * (cross-origin cookie issues, disconnects, etc.).
 *
 * Polls `/permission/user` on an interval — refreshes menus and triggers the
 * credentials interceptor when the session was revoked (deactivated user).
 */
@Injectable({ providedIn: 'root' })
export class RbacLiveSyncService implements OnDestroy {
  private readonly permissionService = inject(PermissionService);
  private readonly platformNotifications = inject(PlatformNotificationService);

  private pollSub?: Subscription;
  /** 15s — fast enough for status changes without heavy load. */
  private readonly pollIntervalMs = 15_000;

  start(): void {
    this.platformNotifications.connectLive();
    this.stop();
    this.pollSub = timer(this.pollIntervalMs, this.pollIntervalMs)
      .pipe(
        switchMap(() =>
          // Belt and braces: refreshPermissions already swallows errors, but an error
          // reaching the timer would unsubscribe it permanently and silently stop live
          // sync for the rest of the session.
          this.permissionService.refreshPermissions().pipe(catchError(() => of(undefined))),
        ),
      )
      .subscribe();
  }

  stop(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
